import { useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useLanguage } from "@/contexts/LanguageContext";
import { translations } from "@/translations";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { HealthConsentField } from "@/components/HealthConsentField";
import { DataProtectionNotice } from "@/components/DataProtectionNotice";
import { TermsLabel } from "@/components/TermsLabel";
import { faltaConsentimientoSalud, textosSalud } from "@/lib/healthConsent";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Snowflake, Gift } from "lucide-react";

const ChristmasCampRegistrationForm = () => {
  const { language } = useLanguage();
  const t = translations[language].christmasCamp;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formSchema = z.object({
    parentName: z.string().min(2, { message: "Name is required" }).max(100),
    parentEmail: z.string().email({ message: "Valid email is required" }).max(255),
    parentPhone: z.string().min(9, { message: "Valid phone is required" }).max(20),
    childName: z.string().min(2, { message: "Child name is required" }).max(100),
    childAge: z.number().int().min(3).max(12),
    childNotes: z.string().max(1000).optional(),
    weeks: z.array(z.string()).min(1, { message: "Select at least one period" }),
    earlyDropoff: z.boolean().default(false),
    latePickup: z.boolean().default(false),
    isGandaraMember: z.boolean().default(false),
    acceptTerms: z.boolean().refine(val => val, { message: "You must accept the terms" }),
    healthConsent: z.boolean().default(false)
  }).refine((v) => !faltaConsentimientoSalud(v.childNotes, v.healthConsent), {
    path: ["healthConsent"],
    message: textosSalud(language).error
  });

  type FormValues = z.infer<typeof formSchema>;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      parentName: "",
      parentEmail: "",
      parentPhone: "",
      childName: "",
      childAge: 3,
      childNotes: "",
      weeks: [],
      earlyDropoff: false,
      latePickup: false,
      isGandaraMember: false,
      acceptTerms: false,
      healthConsent: false
    }
  });

  const handleSubmit = async (values: FormValues) => {
    setIsSubmitting(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('send-christmas-camp-registration', {
        body: {
          ...values,
          language
        }
      });

      if (error) throw error;

      toast.success(
        language === 'es' ? "Inscripción enviada con éxito. Te hemos enviado un email de confirmación." : 
        language === 'pt' ? "Inscrição enviada com sucesso. Enviamos um email de confirmação." : 
        language === 'fr' ? "Inscription envoyée avec succès. Nous vous avons envoyé un email de confirmation." : 
        "Registration submitted successfully. We've sent you a confirmation email."
      );
      
      form.reset();
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error(
        language === 'es' ? "Error al enviar la inscripción. Inténtalo de nuevo." : 
        language === 'pt' ? "Erro ao enviar a inscrição. Tente novamente." : 
        language === 'fr' ? "Erreur lors de l'envoi de l'inscription. Réessayez." : 
        "Error submitting registration. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const availableWeeks = [
    { id: "week1", label: language === 'es' ? "22-27 Dic" : language === 'pt' ? "22-27 Dez" : language === 'fr' ? "22-27 Déc" : "Dec 22-27" },
    { id: "week2", label: language === 'es' ? "29 Dic - 3 Ene" : language === 'pt' ? "29 Dez - 3 Jan" : language === 'fr' ? "29 Déc - 3 Jan" : "Dec 29 - Jan 3" },
    { id: "week3", label: language === 'es' ? "5-7 Ene" : language === 'pt' ? "5-7 Jan" : language === 'fr' ? "5-7 Jan" : "Jan 5-7" },
    { id: "complete", label: language === 'es' ? "Campamento completo" : language === 'pt' ? "Acampamento completo" : language === 'fr' ? "Camp complet" : "Full camp" }
  ];

  const labels = {
    parentInfo: language === 'es' ? "Información del padre/madre/tutor" : language === 'pt' ? "Informação do pai/mãe/tutor" : language === 'fr' ? "Informations du parent/tuteur" : "Parent/Guardian Information",
    childInfo: language === 'es' ? "Información del niño/a" : language === 'pt' ? "Informação da criança" : language === 'fr' ? "Informations de l'enfant" : "Child Information",
    weeksSelection: language === 'es' ? "Selección de fechas" : language === 'pt' ? "Seleção de datas" : language === 'fr' ? "Sélection des dates" : "Date Selection",
    options: language === 'es' ? "Opciones de conciliación" : language === 'pt' ? "Opções de conciliação" : language === 'fr' ? "Options de conciliation" : "Extended Hours Options",
    membership: language === 'es' ? "Membresía de Gándara" : language === 'pt' ? "Membro da Gándara" : language === 'fr' ? "Membre de Gándara" : "Gándara Membership",
    fullName: language === 'es' ? "Nombre completo" : language === 'pt' ? "Nome completo" : language === 'fr' ? "Nom complet" : "Full Name",
    phone: language === 'es' ? "Teléfono" : language === 'pt' ? "Telefone" : language === 'fr' ? "Téléphone" : "Phone",
    childName: language === 'es' ? "Nombre del niño/a" : language === 'pt' ? "Nome da criança" : language === 'fr' ? "Nom de l'enfant" : "Child's Name",
    age: language === 'es' ? "Edad" : language === 'pt' ? "Idade" : language === 'fr' ? "Âge" : "Age",
    notes: language === 'es' ? "Información adicional (alergias, necesidades especiales, etc.)" : language === 'pt' ? "Informação adicional (alergias, necessidades especiais, etc.)" : language === 'fr' ? "Informations supplémentaires (allergies, besoins spéciaux, etc.)" : "Additional Information (allergies, special needs, etc.)",
    earlyDropoff: language === 'es' ? "Aula madruga (08:30 - 09:00)" : language === 'pt' ? "Aula madrugada (08:30 - 09:00)" : language === 'fr' ? "Accueil matinal (08:30 - 09:00)" : "Early drop-off (08:30 - 09:00)",
    latePickup: language === 'es' ? "Aula salida (14:00 - 15:30)" : language === 'pt' ? "Aula saída (14:00 - 15:30)" : language === 'fr' ? "Sortie tardive (14:00 - 15:30)" : "Late pickup (14:00 - 15:30)",
    memberYes: language === 'es' ? "Sí, somos miembros de Gándara" : language === 'pt' ? "Sim, somos membros da Gándara" : language === 'fr' ? "Oui, nous sommes membres de Gándara" : "Yes, we are Gándara members",
    memberNo: language === 'es' ? "No somos miembros de Gándara" : language === 'pt' ? "Não somos membros da Gándara" : language === 'fr' ? "Nous ne sommes pas membres de Gándara" : "We are not Gándara members",
    submit: language === 'es' ? "Enviar inscripción" : language === 'pt' ? "Enviar inscrição" : language === 'fr' ? "Envoyer l'inscription" : "Submit Registration",
    submitting: language === 'es' ? "Enviando..." : language === 'pt' ? "Enviando..." : language === 'fr' ? "Envoi en cours..." : "Submitting..."
  };

  return (
    <div className="bg-card p-6 rounded-xl shadow-md border-2 border-red-500/20">
      <div className="flex items-center justify-center gap-3 mb-6">
        <Snowflake className="w-6 h-6 text-red-500" />
        <h3 className="text-2xl font-semibold text-primary">
          {language === 'es' ? "Formulario de Inscripción" : 
           language === 'pt' ? "Formulário de Inscrição" : 
           language === 'fr' ? "Formulaire d'Inscription" : 
           "Registration Form"}
        </h3>
        <Gift className="w-6 h-6 text-green-600" />
      </div>
      
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-8">
          {/* Parent Information */}
          <div>
            <h4 className="text-lg font-medium mb-4 text-foreground">{labels.parentInfo}</h4>
            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="parentName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{labels.fullName}</FormLabel>
                    <FormControl>
                      <Input placeholder="María García" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="parentEmail"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="maria@example.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="parentPhone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{labels.phone}</FormLabel>
                    <FormControl>
                      <Input placeholder="+34 600 123 456" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
          
          {/* Child Information */}
          <div>
            <h4 className="text-lg font-medium mb-4 text-foreground">{labels.childInfo}</h4>
            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="childName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{labels.childName}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="childAge"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{labels.age}</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        min={3} 
                        max={12}
                        {...field}
                        onChange={(e) => field.onChange(parseInt(e.target.value) || 3)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <div className="mt-4">
              <FormField
                control={form.control}
                name="childNotes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{labels.notes}</FormLabel>
                    <FormControl>
                      <Textarea {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="mt-4">
              <HealthConsentField control={form.control} name="healthConsent" />
            </div>
          </div>
          
          {/* Weeks Selection */}
          <div>
            <h4 className="text-lg font-medium mb-4 text-foreground">{labels.weeksSelection}</h4>
            <FormField
              control={form.control}
              name="weeks"
              render={() => (
                <FormItem>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {availableWeeks.map((week) => (
                      <FormField
                        key={week.id}
                        control={form.control}
                        name="weeks"
                        render={({ field }) => {
                          return (
                            <FormItem
                              key={week.id}
                              className="flex flex-row items-start space-x-3 space-y-0 p-3 rounded-lg border border-green-600/20 hover:bg-muted/50 transition-colors"
                            >
                              <FormControl>
                                <Checkbox
                                  checked={field.value?.includes(week.id)}
                                  onCheckedChange={(checked) => {
                                    if (week.id === 'complete') {
                                      return checked
                                        ? field.onChange(['complete'])
                                        : field.onChange([]);
                                    }
                                    if (field.value?.includes('complete')) {
                                      return checked
                                        ? field.onChange([week.id])
                                        : field.onChange([]);
                                    }
                                    return checked
                                      ? field.onChange([...field.value, week.id])
                                      : field.onChange(field.value?.filter((value) => value !== week.id));
                                  }}
                                />
                              </FormControl>
                              <FormLabel className="font-normal cursor-pointer">
                                {week.label}
                              </FormLabel>
                            </FormItem>
                          );
                        }}
                      />
                    ))}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          
          {/* Extended Hours Options */}
          <div>
            <h4 className="text-lg font-medium mb-4 text-foreground">{labels.options}</h4>
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="earlyDropoff"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border border-red-500/20 p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">{labels.earlyDropoff}</FormLabel>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="latePickup"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border border-green-600/20 p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">{labels.latePickup}</FormLabel>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </div>
          
          {/* Gandara Membership */}
          <div>
            <h4 className="text-lg font-medium mb-4 text-foreground">{labels.membership}</h4>
            <FormField
              control={form.control}
              name="isGandaraMember"
              render={({ field }) => (
                <FormItem className="space-y-3">
                  <FormControl>
                    <RadioGroup
                      onValueChange={(value) => field.onChange(value === 'yes')}
                      defaultValue={field.value ? 'yes' : 'no'}
                      className="flex flex-col space-y-1"
                    >
                      <FormItem className="flex items-center space-x-3 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="yes" />
                        </FormControl>
                        <FormLabel className="font-normal">{labels.memberYes}</FormLabel>
                      </FormItem>
                      <FormItem className="flex items-center space-x-3 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="no" />
                        </FormControl>
                        <FormLabel className="font-normal">{labels.memberNo}</FormLabel>
                      </FormItem>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          
          {/* Terms and Conditions */}
          <FormField
            control={form.control}
            name="acceptTerms"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel>
                    <TermsLabel />
                  </FormLabel>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <DataProtectionNotice finalidad="campamento" />

          <Button 
            type="submit" 
            disabled={isSubmitting}
            className="w-full bg-red-500 hover:bg-red-600 text-white"
          >
            {isSubmitting ? labels.submitting : labels.submit}
          </Button>
        </form>
      </Form>
    </div>
  );
};

export default ChristmasCampRegistrationForm;
